import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAgendamentoStepIndicator } from "../../../hooks/useAgendamentoStepIndicator";
import { useContextSite } from "../../../context/Context";
import { useDownloadModeloPlanilha } from "../../../hooks/queries/loteAgendamento/useDownloadModeloPlanilha/useDownloadModeloPlanilha";
import { useValidarPlanilhaLote } from "../../../hooks/queries/loteAgendamento/useValidarPlanilhaLote/useValidarPlanilhaLote";
import { useImportarPlanilhaLote } from "../../../hooks/queries/loteAgendamento/useImportarPlanilhaLote/useImportarPlanilhaLote";
import { useVisualizarLoteAgendamento } from "../../../hooks/queries/loteAgendamento/useVisualizarLoteAgendamento/useVisualizarLoteAgendamento";
import { Tracking } from "../../../services/Tracking";
import { FormaPagamentoEnum } from "../../../enums/formaPagamento";
import { SERVICO_POR_TIPO_SERVICO } from "../../../constants/servico_lote";
import { MODALIDADE_POR_TIPO_ATENDIMENTO } from "../../../constants/modalidade_lote";
import {
  ITransferenciaValidacaoDTO,
  TipoServicoLote,
} from "../../../types/loteAgendamento";

const COLUNAS_POR_TIPO_SERVICO: Record<TipoServicoLote, string[]> = {
  transferencia: [
    "placa",
    "renavam",
    "nome",
    "cpf_cnpj",
    "telefone",
    "email",
    "cep",
    "rua",
    "numero",
    "complemento",
    "bairro",
    "cidade",
    "uf",
  ],
  "primeiro-emplacamento": [
    "chassi",
    "nome",
    "cpf_cnpj",
    "telefone",
    "email",
    "cep",
    "rua",
    "numero",
    "complemento",
    "bairro",
    "cidade",
    "uf",
  ],
};

export type FormaPagamentoDisponivel =
  | FormaPagamentoEnum.PIX
  | FormaPagamentoEnum.BOLETO;

export const useBatchSpreadsheetImport = () => {
  const navigate = useNavigate();
  const { uuidAgendamento, tipoServico } = useParams<{
    uuidAgendamento: string;
    tipoServico: TipoServicoLote;
  }>();
  const stepIndicator = useAgendamentoStepIndicator();
  const { setIsLoad } = useContextSite();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<
    ITransferenciaValidacaoDTO[] | null
  >(null);
  const [formaPagamento, setFormaPagamento] = useState<FormaPagamentoDisponivel>(
    FormaPagamentoEnum.PIX,
  );

  const { mutate: downloadModeloPlanilha } = useDownloadModeloPlanilha();
  const { mutate: validarPlanilhaLote } = useValidarPlanilhaLote();
  const { mutate: importarPlanilhaLote } = useImportarPlanilhaLote();
  const { data: loteAgendamento, isLoading: isLoadingResumo } =
    useVisualizarLoteAgendamento(uuidAgendamento);

  useEffect(() => {
    setIsLoad(isLoadingResumo);
  }, [isLoadingResumo]);

  const modalidade = loteAgendamento
    ? MODALIDADE_POR_TIPO_ATENDIMENTO[loteAgendamento.tipoAtendimento]
    : undefined;
  const local = loteAgendamento?.cidade;
  const servico = SERVICO_POR_TIPO_SERVICO[tipoServico as TipoServicoLote];

  const identificador: "placa" | "chassi" =
    tipoServico === "transferencia" ? "placa" : "chassi";
  const colunas = COLUNAS_POR_TIPO_SERVICO[tipoServico as TipoServicoLote];
  const todosAprovados =
    validationResult !== null &&
    validationResult.every((resultado) => resultado.status === "APROVADO");

  function handleFileSelected(file: File) {
    setSelectedFile(file);
    setIsLoad(true);
    validarPlanilhaLote(
      { uuid: uuidAgendamento ?? "", tipoServico: tipoServico as TipoServicoLote, planilha: file },
      {
        onSuccess: ({ data }) => setValidationResult(data),
        onError: ({
          response: {
            data: { mensagem },
          },
        }: any) => {
          Tracking.error(
            `mapa:agendamento-lote/servicos/${tipoServico}:validar-planilha-falhou`,
            { mensagem },
          );
        },
        onSettled: () => setIsLoad(false),
      },
    );
  }

  function handleRemover() {
    setSelectedFile(null);
    setValidationResult(null);
    setFormaPagamento(FormaPagamentoEnum.PIX);
  }

  function handleDownloadModelo() {
    downloadModeloPlanilha(tipoServico as TipoServicoLote, {
      onSuccess: ({ data }) => {
        const url = window.URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "modelo.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      },
    });
  }

  function handleImportar() {
    if (!todosAprovados || !selectedFile) return;

    setIsLoad(true);
    importarPlanilhaLote(
      {
        uuid: uuidAgendamento ?? "",
        tipoServico: tipoServico as TipoServicoLote,
        planilha: selectedFile,
        formaPagamento,
      },
      {
        onSuccess: () => {
          navigate(
            `/agendamento-lote/${uuidAgendamento}/agendamentos-criados?tipoServico=${tipoServico}`,
          );
        },
        onError: ({
          response: {
            data: { mensagem },
          },
        }: any) => {
          Tracking.error(
            `mapa:agendamento-lote/servicos/${tipoServico}:importar-planilha-falhou`,
            { mensagem },
          );
        },
        onSettled: () => setIsLoad(false),
      },
    );
  }

  return {
    stepIndicator,
    tipoServico: tipoServico as TipoServicoLote,
    modalidade,
    local,
    servico,
    colunas,
    identificador,
    selectedFile,
    validationResult,
    formaPagamento,
    setFormaPagamento,
    todosAprovados,
    handleFileSelected,
    handleRemover,
    handleDownloadModelo,
    handleImportar,
  };
};
